import { useEffect, useState, useRef } from "react";
import { Warning, CaretDown, CaretRight, ArrowsClockwise, GitFork, ArrowBendUpRight, Plus } from "@phosphor-icons/react";
import { masterCrossWired, masterDewire, adminCollegesLiteSearch } from "../lib/api";
import { toast } from "sonner";

function DewireRow({ m, onChanged }) {
  const [tab, setTab] = useState(null);            // null | "reassign" | "create"
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [pick, setPick] = useState(null);
  const [newName, setNewName] = useState(m.seat_matrix_by_bracket?.[m.foreign_code] || "");
  const [location, setLocation] = useState("");
  const [state, setState] = useState(m.state || "");
  const [checked, setChecked] = useState({});
  const [busy, setBusy] = useState(false);
  const deb = useRef(null);

  const matchNames = () => (m.raw_clusters || []).filter((_, i) => checked[i]).map((c) => c.name);

  const search = (val) => {
    setQ(val); setPick(null);
    if (deb.current) clearTimeout(deb.current);
    if (val.trim().length < 2) { setResults([]); return; }
    deb.current = setTimeout(async () => {
      try {
        const res = await adminCollegesLiteSearch(val.trim(), m.state);
        setResults((res.data?.results || []).filter((r) => r.college_code !== m.college_code));
      } catch { setResults([]); }
    }, 300);
  };

  const run = async (mode) => {
    const body = { foreign_code: m.foreign_code, mode, match_names: matchNames() };
    if (mode === "reassign") {
      if (!pick) { toast.error("Pick a destination college for B"); return; }
      body.dest_code = pick.college_code;
    } else {
      if (!newName.trim()) { toast.error("Enter a name for the new college B"); return; }
      body.new_name = newName.trim(); body.location = location.trim() || null; body.state = state.trim() || null;
    }
    const destLabel = mode === "reassign" ? `${pick.college_code} (${pick.primary_name})` : "NEW master " + m.foreign_code;
    if (!window.confirm(`De-wire ${m.foreign_code} off ${m.college_code} → ${destLabel}? ${m.college_code} restored to its own code. Re-derives both.`)) return;
    setBusy(true);
    try {
      const { data } = await masterDewire(m.college_code, body);
      toast.success(`De-wired → ${data.dest_code} (moved ${data.raw_rows_moved} rows, ${data.aliases_moved} aliases; rebuilt ${data.mcc_summary_rows_rebuilt})`);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "De-wire failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-2 pt-2 border-t border-amber-100">
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-bold uppercase text-zinc-400">Fix B ({m.foreign_code}):</span>
        <button onClick={() => setTab(tab === "reassign" ? null : "reassign")} disabled={busy}
          data-testid={`dewire-reassign-toggle-${m.college_code}`}
          className={`inline-flex items-center gap-1 h-6 px-2 rounded border text-[11px] font-bold ${tab === "reassign" ? "bg-sky-600 text-white border-sky-600" : "border-sky-300 text-sky-700 hover:bg-sky-50"}`}>
          <ArrowBendUpRight size={12} weight="bold" /> Reassign to existing
        </button>
        <button onClick={() => setTab(tab === "create" ? null : "create")} disabled={busy}
          data-testid={`dewire-create-toggle-${m.college_code}`}
          className={`inline-flex items-center gap-1 h-6 px-2 rounded border text-[11px] font-bold ${tab === "create" ? "bg-indigo-600 text-white border-indigo-600" : "border-indigo-300 text-indigo-700 hover:bg-indigo-50"}`}>
          <Plus size={12} weight="bold" /> De-link &amp; create new
        </button>
      </div>

      {(tab === "reassign" || tab === "create") && (
        <div className="mt-1.5 p-2 rounded-md border border-zinc-200 bg-white space-y-2">
          {(m.raw_clusters || []).length > 0 && (
            <div>
              <div className="text-[10px] font-bold uppercase text-zinc-400 mb-0.5">Move which raw clusters to B? (leave unchecked if B has no allotments here)</div>
              {m.raw_clusters.map((c, i) => (
                <label key={i} className="flex items-start gap-1.5 text-[11px] cursor-pointer" data-testid={`dewire-cluster-${m.college_code}-${i}`}>
                  <input type="checkbox" checked={!!checked[i]} className="mt-0.5"
                    onChange={(e) => setChecked((p) => ({ ...p, [i]: e.target.checked }))} />
                  <span className="font-mono text-zinc-500 shrink-0">{c.count}</span>
                  <span className="text-zinc-700 break-all">{c.name}</span>
                </label>
              ))}
            </div>
          )}
          {tab === "reassign" ? (
            <div className="flex items-center gap-1.5">
              <div className="relative flex-1">
                <input
                  data-testid={`dewire-dest-${m.college_code}`}
                  value={pick ? `${pick.college_code} · ${pick.primary_name}` : q}
                  onChange={(e) => search(e.target.value)} disabled={busy}
                  placeholder="search destination master for B by name/code…"
                  className="w-full h-6 px-1.5 rounded border border-zinc-300 text-[11px]" />
                {!pick && results.length > 0 && (
                  <div className="absolute z-20 mt-0.5 w-full max-h-56 overflow-auto bg-white border border-zinc-200 rounded-md shadow-lg" data-testid={`dewire-results-${m.college_code}`}>
                    {results.map((r) => (
                      <button key={r.college_code} onClick={() => { setPick(r); setResults([]); }}
                        data-testid={`dewire-opt-${r.college_code}`}
                        className="block w-full text-left px-2 py-1 text-[11px] hover:bg-sky-50 border-b border-zinc-50">
                        <code className="font-mono text-sky-700">{r.college_code}</code> · {r.primary_name}
                        <span className="text-zinc-400"> · {r.city || r.state}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button onClick={() => run("reassign")} disabled={busy || !pick} data-testid={`dewire-reassign-submit-${m.college_code}`}
                className="inline-flex items-center gap-1 h-6 px-2 rounded bg-sky-600 text-white text-[11px] font-bold hover:bg-sky-700 disabled:opacity-40">
                <ArrowBendUpRight size={12} weight="bold" /> Reassign B → dest
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-1.5">
              <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="new college B name"
                data-testid={`dewire-name-${m.college_code}`} className="col-span-2 h-6 px-1.5 rounded border border-zinc-300 text-[11px]" />
              <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="location"
                data-testid={`dewire-location-${m.college_code}`} className="h-6 px-1.5 rounded border border-zinc-300 text-[11px]" />
              <input value={state} onChange={(e) => setState(e.target.value)} placeholder="state"
                data-testid={`dewire-state-${m.college_code}`} className="h-6 px-1.5 rounded border border-zinc-300 text-[11px]" />
              <button onClick={() => run("create_new")} disabled={busy} data-testid={`dewire-create-submit-${m.college_code}`}
                className="col-span-2 inline-flex items-center justify-center gap-1 h-6 px-2 rounded bg-indigo-600 text-white text-[11px] font-bold hover:bg-indigo-700 disabled:opacity-40">
                <Plus size={12} weight="bold" /> Create master {m.foreign_code} &amp; move B
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function CrossWiredPanel() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await masterCrossWired();
      setRows(data.masters || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load cross-wired masters");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open && rows === null) load(); }, [open]); // eslint-disable-line

  return (
    <div className="border-2 border-amber-200 rounded-xl overflow-hidden bg-white mb-5" data-testid="cross-wired-card">
      <button onClick={() => setOpen((o) => !o)} data-testid="cross-wired-toggle"
        className="w-full px-3 py-2.5 flex items-center gap-2 border-b border-amber-100 bg-amber-50/60 hover:bg-amber-50">
        {open ? <CaretDown size={16} weight="bold" /> : <CaretRight size={16} weight="bold" />}
        <Warning size={16} weight="fill" className="text-amber-600" />
        <span className="text-xs font-bold uppercase tracking-wider text-amber-800">
          Cross-wired masters (two MCC colleges on one master)
        </span>
        {rows !== null && (
          <span className="ml-1 text-[11px] font-bold px-1.5 py-0.5 rounded-full bg-amber-200 text-amber-900" data-testid="cross-wired-count">
            {rows.length}
          </span>
        )}
        {open && (
          <span onClick={(e) => { e.stopPropagation(); load(); }} data-testid="cross-wired-refresh"
            className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 hover:text-amber-900">
            <ArrowsClockwise size={12} weight="bold" /> refresh
          </span>
        )}
      </button>

      {open && (
        <div className="p-3">
          <p className="text-[11px] text-zinc-500 mb-3 max-w-3xl">
            Each row is a master whose <code className="text-amber-700">college_code (A)</code> is a 6-digit MCC code but
            whose <code className="text-amber-700">codes.MCC (B)</code> is a <b>different</b> college's code — so college B's
            seats/allotments collapse onto A. Eyeball the two institutes below; the fix is to keep A on this master and
            de-link B into its own master coded B.
          </p>
          {loading && <div className="text-xs text-zinc-400">loading…</div>}
          {rows !== null && rows.length === 0 && !loading && (
            <div className="text-xs text-emerald-600 font-semibold">No cross-wired masters — all clean. 🎉</div>
          )}
          <div className="space-y-2">
            {(rows || []).map((m) => (
              <div key={m.college_code} className="rounded-lg border border-amber-100 bg-amber-50/30 p-2.5" data-testid={`cross-wired-row-${m.college_code}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <GitFork size={14} weight="bold" className="text-amber-600" />
                  <span className="text-[10px] font-bold uppercase text-zinc-400">{m.state}</span>
                  <span className="text-xs font-mono font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">A · {m.own_code}</span>
                  <span className="text-xs text-zinc-700 font-semibold">{m.primary_name}</span>
                  <span className="text-[11px] text-zinc-400">holds foreign</span>
                  <span className="text-xs font-mono font-bold px-1.5 py-0.5 rounded bg-rose-100 text-rose-800">B · {m.foreign_code}</span>
                </div>
                <div className="mt-1.5 grid md:grid-cols-2 gap-2">
                  <div>
                    <div className="text-[10px] font-bold uppercase text-zinc-400 mb-0.5">Seat-matrix names by bracket</div>
                    {Object.keys(m.seat_matrix_by_bracket || {}).length === 0
                      ? <div className="text-[11px] text-zinc-400">—</div>
                      : Object.entries(m.seat_matrix_by_bracket).map(([b, nm]) => (
                        <div key={b} className="text-[11px]">
                          <span className={`font-mono font-bold ${b === m.foreign_code ? "text-rose-700" : b === m.own_code ? "text-emerald-700" : "text-zinc-500"}`}>({b})</span>{" "}
                          <span className="text-zinc-700">{nm}</span>
                        </div>
                      ))}
                  </div>
                  <div>
                    <div className="text-[10px] font-bold uppercase text-zinc-400 mb-0.5">Raw allotment clusters under A ({m.raw_total})</div>
                    {(m.raw_clusters || []).length === 0
                      ? <div className="text-[11px] text-zinc-400">no MCC allotments under A</div>
                      : m.raw_clusters.map((c, i) => (
                        <div key={i} className="text-[11px]">
                          <span className="font-mono text-zinc-500">{c.count}</span>{" "}
                          <span className="text-zinc-700 break-all">{c.name}</span>
                        </div>
                      ))}
                  </div>
                </div>
                <DewireRow m={m} onChanged={load} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
